import {useMutation, useQueryClient} from 'react-query';

import {type IStandardErrorResponse} from 'src/api/common';
import {
    type EMessageChannel,
    EMessageDirection,
    EMessageSentStatus,
    type IMessage,
    type ISendMessageResponse
} from 'src/services/api/messages/types';
import {useNotifications} from 'src/notifications';
import {apiClient} from 'src/services/api/client';
import {queryKeys} from 'src/services/api/constants';
import {getApiErrorMessage} from 'src/services/api/utils';
import {
    type IGetLeadMessagesWithAttachmentsResponse,
    useLoggedInUser
} from 'src/services/api';

export const SEND_MESSAGE_PATH = 'api/landlord/messages/send';

export type ISendMessageRequest = {
    leadUuid: string;
    channel: EMessageChannel;
    message: string;
    attachments?: File[];
}

export const useSendMessage = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { data: loggedInUser } = useLoggedInUser();

    return useMutation<
        ISendMessageResponse['new_message'],
        IStandardErrorResponse,
        ISendMessageRequest
    >(
        async (payload) => {
            const formData = new FormData();
            formData.append('lead_uuid', payload.leadUuid);
            formData.append('channel', payload.channel);
            formData.append('contents', payload.message);
            if (payload.attachments) {
                payload.attachments.forEach((attachment, index) => {
                    formData.append(`attachments${index}`, attachment, attachment.name);
                });
            }

            const tempMessage: IMessage = {
                uuid: 'placeholder-' + Math.random(),
                owner: loggedInUser!.id,
                channel: payload.channel,
                contents: payload.message,
                direction: EMessageDirection.OUT,
                sent_status: EMessageSentStatus.SENT,
                is_sent_by_automation: false,
                is_rental_application_request: false,
                inserted_at: new Date(),
            };

            queryClient.setQueryData(
                [queryKeys.messages, payload.leadUuid],
                (oldData: IGetLeadMessagesWithAttachmentsResponse | IMessage[] | undefined): IGetLeadMessagesWithAttachmentsResponse | IMessage[] => {
                    if (!oldData) {
                        return [tempMessage];
                    }
                    if (Array.isArray(oldData)) {
                        return [...oldData, tempMessage];
                    }
                    return {
                        ...oldData,
                        messages: [...oldData.messages, tempMessage],
                    };
                }
            );

            const res = await apiClient(SEND_MESSAGE_PATH, {
                method: 'post',
                body: formData,
                timeout: 30000,
            }).json<ISendMessageResponse>();

            return res.new_message;
        }, {
            onSuccess: (_data, variables) => {
                addNotification('Message sent', 'success');
                queryClient.invalidateQueries(queryKeys.conversations);
                queryClient.invalidateQueries([queryKeys.messages, variables.leadUuid]);
                queryClient.invalidateQueries(queryKeys.achievements);
            },
            onError: (error, payload) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to send message';
                addNotification(errorMessage, 'error');
                queryClient.invalidateQueries([queryKeys.messages, payload.leadUuid]);
            }
        }
    );
};
